'use client';

import { Discord } from '@velocity-exchange/icons';
import ErrorPageBackdrop from 'src/components/ErrorPageBackdrop';
import { VELOCITY_DISCORD_URL } from 'src/constants/constants';

/**
 * This is the global error page that is displayed when an error occurs in the root layout component only.
 * It replaces the entire document (including the root layout), so the Tailwind stylesheet is not available
 * here — layout is done with ad-hoc inline styles. The decorative backdrop and the Discord icon are plain
 * SVGs with no styling dependencies, so they render correctly here and mirror the main `ErrorPage` design.
 */
const GlobalError = () => {
	return (
		<html>
			<head>
				<style>{`
					* {
						margin: 0;
						padding: 0;
						box-sizing: border-box;
					}
					body {
						font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
						position: relative;
						overflow: hidden;
						background-color: #05060c;
						color: #e4e4e9;
						min-height: 100vh;
						display: grid;
						place-content: center;
						padding: 20px;
					}
					.backdrop {
						position: absolute;
						top: calc(50% - 84px);
						left: 50%;
						transform: translate(-50%, -50%);
						width: min(760px, 120vw);
						aspect-ratio: 760 / 336;
						pointer-events: none;
						z-index: 0;
					}
					.container {
						position: relative;
						z-index: 1;
						text-align: center;
						max-width: 488px;
						width: 100%;
					}
					h1 {
						font-size: 48px;
						font-weight: bold;
						color: #e4e4e9;
						margin-bottom: 24px;
					}
					h1 .accent {
						color: #44be4d;
					}
					@media (min-width: 640px) {
						h1 {
							font-size: 90px;
						}
					}
					.subtitle {
						font-size: 24px;
						font-weight: 600;
						margin-bottom: 24px;
						color: #e4e4e9;
					}
					@media (min-width: 640px) {
						.subtitle {
							font-size: 32px;
						}
					}
					.description {
						font-size: 14px;
						line-height: 1.5;
						margin-bottom: 24px;
						color: #838ea4;
					}
					.description a {
						color: #e4e4e9;
						text-decoration: underline;
					}
					.buttons {
						display: flex;
						align-items: center;
						justify-content: center;
						gap: 16px;
					}
					.icon-button {
						display: inline-grid;
						place-items: center;
						width: 42px;
						height: 42px;
						border-radius: 9999px;
						border: 1px solid #ffffff;
						color: #e4e4e9;
						text-decoration: none;
						transition: background-color 0.2s;
					}
					.icon-button:hover {
						background-color: rgba(255, 255, 255, 0.08);
					}
					.button {
						display: inline-block;
						padding: 12px 24px;
						background-color: #ffffff;
						color: #05060c;
						text-decoration: none;
						border-radius: 9999px;
						font-size: 14px;
						font-weight: 500;
						transition: background-color 0.2s;
					}
					.button:hover {
						background-color: #e4e4e9;
					}
				`}</style>
			</head>

			<body>
				<div className="backdrop">
					<ErrorPageBackdrop />
				</div>

				<div className="container">
					<h1>
						Wh<span className="accent">oops!</span>
					</h1>
					<p className="subtitle">Failed to complete action</p>
					<p className="description">
						Something&apos;s gone wrong with the action you&apos;ve performed.
						<br />
						Go back to the Homepage and try again.
						<br />
						If this problem persists, please reach out on{' '}
						<a
							href={VELOCITY_DISCORD_URL}
							target="_blank"
							rel="noopener noreferrer"
						>
							Discord
						</a>
						.
					</p>
					<div className="buttons">
						<a
							className="icon-button"
							href={VELOCITY_DISCORD_URL}
							target="_blank"
							rel="noopener noreferrer"
							aria-label="Join our Discord"
						>
							<Discord size={20} />
						</a>
						<a href="/" className="button">
							Go to Homepage
						</a>
					</div>
				</div>
			</body>
		</html>
	);
};

export default GlobalError;
